// src/salesAssessment/SalesAssessmentConfiguration.js
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Send as SendIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TestReminderFields, { DEFAULT_REMINDER_CONFIG } from '../layouts/TestReminderFields';
import useStore from '../config/store';
import api from '../config/api';

const applicantFilter = createFilterOptions({
  stringify: (option) => `${option?.name || ''}`,
  trim: true,
});

const SalesAssessmentConfiguration = () => {
  const { t } = useTranslation();
  const { authToken, user, project, organization, showAlert, language } = useStore();

  // Estados del formulario
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [reminderConfig, setReminderConfig] = useState(DEFAULT_REMINDER_CONFIG);

  // Estados de datos
  const [positions, setPositions] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);

  // Estados de carga
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [sending, setSending] = useState(false);

  // Cargar cargos
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        setLoadingPositions(true);
        const { data } = await api.get(
          `/positions?projectId=${project}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (data.response?.status_response === "OK") {
          setPositions(data.response.data || []);
        }
      } catch (error) {
        console.error("Error loading positions:", error);
        showAlert(t('generalApiError'), 'error');
      } finally {
        setLoadingPositions(false);
      }
    };

    if (project && authToken) {
      fetchPositions();
    }
  }, [project, authToken]);

  // Cargar postulantes
  useEffect(() => {
    const fetchApplicants = async () => {
      try {
        setLoadingApplicants(true);
        const { data } = await api.get(
          `/applicants?projectId=${project}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (data.response?.status_response === "OK") {
          setApplicants((data.response.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })));
        }
      } catch (error) {
        console.error("Error loading applicants:", error);
        showAlert(t('generalApiError'), 'error');
      } finally {
        setLoadingApplicants(false);
      }
    };

    if (project && authToken) {
      fetchApplicants();
    }
  }, [project, authToken]);

  // Ordenar postulantes cuando se selecciona un cargo
  useEffect(() => {
    if (selectedPosition) {
      const positionId = selectedPosition.uid || selectedPosition.id;

      const withPosition = [];
      const withoutPosition = [];

      applicants.forEach(applicant => {
        const belongsToPosition = applicant.positions?.some(pos =>
          (pos.positionId === positionId) ||
          (pos.uid === positionId) ||
          (pos.id === positionId)
        );

        if (belongsToPosition) {
          withPosition.push(applicant);
        } else {
          withoutPosition.push(applicant);
        }
      });

      setFilteredApplicants([...withPosition, ...withoutPosition]);
    } else {
      setFilteredApplicants(applicants);
    }
  }, [selectedPosition, applicants]);

  // Resetear formulario
  const resetForm = () => {
    setSelectedPosition(null);
    setSelectedApplicant(null);
    setSendEmail(true);
    setReminderConfig(DEFAULT_REMINDER_CONFIG);
  };

  // Manejar envío del formulario
  const handleSendTest = async () => {
    if (!selectedApplicant) {
      showAlert(t('salesAssessment.mustSelectApplicant'), 'warning');
      return;
    }

    try {
      setSending(true);

      const positionData = selectedPosition ? {
        name: selectedPosition.name,
        industry: selectedPosition.industria || '',
        description: selectedPosition.descripcion || '',
      } : {
        name: '',
        industry: '',
        description: '',
      };

      const testData = {
        applicantId: selectedApplicant.id || selectedApplicant.uid,
        applicantName: selectedApplicant.name,
        applicantEmail: selectedApplicant.email,
        positionData: positionData,
        projectId: project,
        organizationId: organization,
        language: language || 'es',
        sendEmail: sendEmail,
        reminderConfig,
      };

      const { data } = await api.post(
        '/sales-assessments',
        testData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.errorCode === 0) {
        showAlert(
          sendEmail
            ? t('salesAssessment.testSentSuccess')
            : t('salesAssessment.testCreatedSuccess'),
          'success'
        );
        resetForm();
      } else {
        showAlert(t('salesAssessment.testSentError'), 'error');
      }

    } catch (error) {
      console.error("Error sending sales assessment:", error);
      showAlert(t('generalApiError'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('salesAssessment.createTest')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('salesAssessment.selectApplicantDescription')}
        </Typography>

        {/* Selector de cargo */}
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            options={positions}
            getOptionLabel={(option) => `${option.name} - ${option.industria || t('salesAssessment.noIndustry')}`}
            value={selectedPosition}
            onChange={(event, newValue) => setSelectedPosition(newValue)}
            loading={loadingPositions}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('salesAssessment.selectPosition')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <WorkIcon sx={{ mr: 1, color: 'action.active' }} />,
                  endAdornment: (
                    <>
                      {loadingPositions ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.industria || t('salesAssessment.noIndustry')}
                  </Typography>
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Mostrar información del cargo seleccionado */}
        {selectedPosition && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>{t('salesAssessment.positionLabel')}</strong> {selectedPosition.name}
            </Typography>
            <Typography variant="body2">
              <strong>{t('salesAssessment.industryLabel')}</strong> {selectedPosition.industria || t('salesAssessment.notSpecified')}
            </Typography>
            {selectedPosition.descripcion && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>{t('salesAssessment.descriptionLabel')}</strong> {selectedPosition.descripcion}
              </Typography>
            )}
          </Alert>
        )}

        {/* Selector de postulante */}
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            options={filteredApplicants}
            getOptionLabel={(option) => option?.name || ''}
            getOptionKey={(option) => option.id ?? option.uid ?? option.email}
            isOptionEqualToValue={(option, value) =>
              (option.id ?? option.uid ?? option.email) ===
              (value.id ?? value.uid ?? value.email)
            }
            filterOptions={applicantFilter}
            value={selectedApplicant}
            onChange={(event, newValue) => setSelectedApplicant(newValue)}
            loading={loadingApplicants}
            noOptionsText={t('noData')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('salesAssessment.selectApplicant', { count: filteredApplicants.length })}
                required
                helperText={
                  selectedPosition
                    ? t('salesAssessment.applicantsSorted')
                    : t('salesAssessment.selectApplicantToSend')
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {loadingApplicants ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id ?? option.uid ?? option.email}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Switch enviar email */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <EmailIcon sx={{ color: sendEmail ? 'primary.main' : 'action.disabled' }} />
          <FormControlLabel
            control={
              <Switch
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  {t('salesAssessment.sendEmailOnCreate')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {sendEmail
                    ? t('salesAssessment.sendEmailOnCreateEnabled')
                    : t('salesAssessment.sendEmailOnCreateDisabled')
                  }
                </Typography>
              </Box>
            }
          />
        </Box>

        {/* Recordatorio automatico: solo aplica cuando el correo lo manda el sistema */}
        <TestReminderFields
          show={sendEmail}
          value={reminderConfig}
          onChange={setReminderConfig}
        />

        {/* Botones de acción */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={resetForm}
            disabled={sending}
          >
            {t('salesAssessment.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handleSendTest}
            disabled={sending || !selectedApplicant}
          >
            {sending
              ? t('salesAssessment.sending')
              : sendEmail
                ? t('salesAssessment.sendTest')
                : t('salesAssessment.createTest')
            }
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SalesAssessmentConfiguration;